import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import { Card } from "react-bootstrap";
import { stafflocations } from "../../services/admin";

/**
 * Staff tracking map for the dashboard. Plots the latest known GPS position of
 * each active staff member (sent by the agent mobile app) on an OpenStreetMap
 * base layer. Refreshes every 60s so the dashboard stays roughly live.
 */
export default function StaffLocationMap() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Init once.
    if (!mapRef.current && mapEl.current) {
      mapRef.current = L.map(mapEl.current, { scrollWheelZoom: false }).setView(
        [11.0, 78.0], // default view (Tamil Nadu); re-centres on markers below
        6
      );
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
      // Leaflet mis-measures inside a freshly-rendered card; nudge it.
      setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 200);
    }

    const pin = (label) =>
      L.divIcon({
        className: "",
        html:
          `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;` +
          `transform:rotate(-45deg);background:#096197;border:2px solid #fff;` +
          `box-shadow:0 2px 5px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center">` +
          `<span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:12px">${label}</span></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 28],
        popupAnchor: [0, -26],
      });

    const load = () => {
      stafflocations().then((res) => {
        setLoading(false);
        if (res?.status !== true || !layerRef.current) return;
        const staff = res.data?.details || [];
        layerRef.current.clearLayers();
        const pts = [];
        staff.forEach((s) => {
          const lat = parseFloat(s.latitude);
          const lng = parseFloat(s.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
          pts.push([lat, lng]);
          const initial = (s.staff_name || "?").charAt(0).toUpperCase();
          const when = s.location_updated_at
            ? moment(s.location_updated_at).fromNow()
            : "unknown";
          L.marker([lat, lng], { icon: pin(initial) })
            .addTo(layerRef.current)
            .bindPopup(
              `<b>${s.staff_name || ""}</b><br/>` +
                `${s.role_name || ""}${s.branch_name ? " &middot; " + s.branch_name : ""}<br/>` +
                `${s.phone ? "📞 " + s.phone + "<br/>" : ""}` +
                `<small>Updated ${when}</small>`
            );
        });
        setCount(pts.length);
        if (pts.length) {
          mapRef.current.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
        }
        setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 100);
      });
    };

    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Staff Location Tracking</h5>
          <span className="text-muted" style={{ fontSize: 13 }}>
            {loading ? "Loading…" : `${count} staff located`}
          </span>
        </div>
        <div
          ref={mapEl}
          style={{ height: 420, width: "100%", borderRadius: 10, overflow: "hidden", zIndex: 0 }}
        />
        {!loading && count === 0 && (
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>
            No staff locations yet. Locations appear once staff use the updated agent app.
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
